import React, { useMemo } from 'react';
import { LegacyColumn as Column } from 'components/MaterialTable/MatDataGridTypes';
import { useAuth } from '../../lib/Auth';
import styled, { createGlobalStyle } from 'styled-components';
import MatDataGrid from 'components/MaterialTable/MatDataGrid';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import { GENERAL_CONSTANTS } from 'constants/general';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import Button from 'components/UI/Button';
import { ORGANIZATION_CONSTANTS } from 'constants/organization';
import useTable from 'hooks/useTable';
import { getAllBusinessUnits } from 'requests/organization';
import { Color } from 'lib/styles';
import { PermissionCode } from 'data/UserPermissions';
import { ReadOnlyWrapper } from 'components/common/ReadOnlyWrapper';
import { useReadPermission } from 'hooks/useUserRoles';
import { BusinessUnit } from 'data/BusinessUnit';
import { isVendorOnlyByBusinessUnit } from 'utils/role';
import { useBusinessUnitBrandFiltering } from 'hooks/useBusinessUnitBrandFiltering';



const StyledButton = styled.button<any>`
  background: none;
  border: none;
  color: #FC780B;
  cursor: pointer;
  padding: 0;
  font-size: inherit;
  margin: 0 5px;
`;


const EmptyStateContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 16px;
  color: #666;
  background: #f9f9f9;
  border-radius: 8px;
  margin: 20px 0;
`;

const CheckboxPaddingStyles = createGlobalStyle`
  .organization-table-wrapper {
    /* Grey out deactivated business unit rows */
    tr:has(.deleted-bu-marker) td {
      opacity: 0.45;
      color: #999;
    }

    tr:has(.deleted-bu-marker) td:last-child {
      opacity: 1;
    }

    /* Disable checkbox on deactivated rows */
    tr:has(.deleted-bu-marker) .MuiCheckbox-root {
      pointer-events: none;
      opacity: 0.3;
    }

    /* Target Material React Table checkbox column selectors */
    .mrt-row-select,
    .mrt-table-head-cell-select,
    .mrt-table-body-cell-select,
    tbody tr td:first-child,
    thead tr th:first-child {
      padding: 8px 12px !important;
      width: 20px !important;
      min-width: 20px !important;
      max-width: 20px !important;
      vertical-align: middle !important;
    }

    .mrt-row-select:has(input[type="checkbox"]),  
    .mrt-table-head-cell-select,  
    .mrt-table-body-cell-select,  
    table thead tr th:first-child, 
    table tbody tr td:first-child,  
    .MuiTableHead-root .MuiTableCell-head:first-child,  
    .MuiTableBody-root .MuiTableCell-body:first-child,  
    th[data-column-id="mrt-row-select"],  
    td[data-column-id="mrt-row-select"] {
      width: 2% !important;
      min-width: 2% !important;
      max-width: 2% !important;
      padding: 4px !important;
    }

    table thead tr th[data-column-id="imagePath"], 
    table tbody tr td[data-column-id="imagePath"],
    .MuiTableHead-root .MuiTableCell-head[data-column-id="imagePath"],
    .MuiTableBody-root .MuiTableCell-body[data-column-id="imagePath"],
    table thead tr th:nth-child(2),
    table tbody tr td:nth-child(2) {
      width: 2% !important;
      min-width: 2% !important;
      max-width: 2% !important;
      padding: 4px 12px !important;
    }
    
    /* Target Material-UI checkbox cells */
    .MuiTableCell-paddingCheckbox {
      padding: 8px 12px !important;
    }
    
    /* Target the actual checkbox component */
    .MuiCheckbox-root {
      padding: 4px !important;
      margin: 0 !important;
    }
    
    /* Additional fallback selectors */
    table tbody tr td:first-child,
    table thead tr th:first-child {
      padding: 8px 12px !important;
    }
    
    /* Material React Table specific class targeting */
    [class*="MuiTableCell"]:first-child {
      padding: 8px 12px !important;
    }
  }
`;

interface Props {
  selectedVendorId?: number;
  searchTerm?: string;
  unitColumns?: Column<any>[];
  setShowPublishModal?: Function;
  setSelectedRows?: Function;
  selectedRows: any[];
  reload?: boolean;
  setReload?: Function;
  handleEdit?: Function;
  handleDelete?: Function;
  handleDeleteMultiple?: (event: any) => void;
  selectedBusinessUnit?: BusinessUnit | null;
}

const BusinessUnitListTable = ({
  selectedVendorId,
  searchTerm = '',
  unitColumns,
  setSelectedRows,
  reload,
  setReload,
  handleEdit,
  handleDelete,
  handleDeleteMultiple,
  selectedRows,
  selectedBusinessUnit
}: Props) => {
  const { token, user } = useAuth();

  // Determine vendor-only status based on selected business unit context
  const isVendorOnlyUser = isVendorOnlyByBusinessUnit(user!, selectedBusinessUnit);

  // Get business unit IDs for filtering when "All" is selected
  const { businessUnitsIds } = useBusinessUnitBrandFiltering({
    selectedBusinessUnit: selectedBusinessUnit || undefined,
    selectedVendorId
  });

  // Convert businessUnitsIds array to comma-separated string for API
  const businessUnitsIdsString = businessUnitsIds.length > 0 ? businessUnitsIds.join(',') : undefined;

  // Use the custom hook for business unit data
  const {
    tableData,
    tableKey,
    loading,
    rowsPerPage,
    currentPage,
    hasInitialLoad,
    sortField,
    sortOrder,
    handleSortChange,
    handlePageChange,
    handleRowsPerPageChange
  } = useTable(
    token,
    getAllBusinessUnits,
    selectedVendorId,
    selectedBusinessUnit?.id,
    searchTerm,
    reload,
    setReload,
    !selectedBusinessUnit?.id ? businessUnitsIdsString : undefined
  );


  // Memoize columns to prevent unnecessary re-renders and ensure consistency
  const { readOnly: isRemoveReadOnly } = useReadPermission(PermissionCode.EDIT_REMOVE_BUSINESS_UNIT);
  const columns = useMemo(() => {
    if (!unitColumns) return [];

    const actionColumn = {
      render: (rowData: any) => {
        if (rowData.deletedOn) return null;
        return (
          <FlexboxContainerComponent>
            <StyledButton onClick={() => handleEdit?.(rowData?.id)}>
              {GENERAL_CONSTANTS.VIEW}
            </StyledButton>
            {!isVendorOnlyUser && (
              <ReadOnlyWrapper readOnly={isRemoveReadOnly}>
                <StyledButton onClick={isRemoveReadOnly ? null : () => handleDelete?.(rowData?.id)}>
                  {GENERAL_CONSTANTS.REMOVE}
                </StyledButton>
              </ReadOnlyWrapper>
            )}
          </FlexboxContainerComponent>
        );
      },
    };

    const imageColumn = {
      render: (rowData: any) => {
        return (
          <>
            {rowData.deletedOn && <span className="deleted-bu-marker" style={{ display: 'none' }} />}
            {!!rowData.imagePath ? (
              <img
                src={process.env.REACT_APP_MEDIA_URL! + rowData.imagePath}
                alt={rowData.name}
                style={{
                  width: '30px',
                  height: '30px',
                  objectFit: 'contain',
                  marginTop: '8px'
                }}
              />
            ) : null}
          </>
        );
      },
    };

    const updatedColumns = unitColumns.map((col) => {
      if (col.field === 'actions') {
        return { ...col, ...actionColumn };
      }

      if (col.field === 'imagePath') {
        const image = unitColumns.find(x => x.title === 'Image');
        return { ...col, ...imageColumn, hidden: image?.hidden };
      }

      // Apply defaultSort only to the currently sorted field to show sort indicator
      if (col.field === sortField && sortOrder) {
        return {
          ...col,
          defaultSort: sortOrder
        };
      }

      // Clear defaultSort for other columns
      return {
        ...col,
        defaultSort: undefined
      };
    });

    return updatedColumns;
  }, [unitColumns, isVendorOnlyUser, handleEdit, handleDelete, sortField, sortOrder, isRemoveReadOnly]);

  // Create a stable table key that includes data state
  const stableTableKey = useMemo(() => {
    return `${tableKey}-${tableData.data.length}-${sortField}-${sortOrder}`;
  }, [tableKey, tableData.data.length, sortField, sortOrder]);

  // Show loading state
  if (loading && !hasInitialLoad) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <LoadingIndicator />
      </div>
    );
  }

  // Show empty state
  if (hasInitialLoad && tableData.totalCount === 0 && !loading) {
    return (
      <EmptyStateContainer>
        <p style={{ margin: 0 }}>{ORGANIZATION_CONSTANTS.PLEASE_CREATE_BUSINESS_UNIT}</p>
      </EmptyStateContainer>
    );
  }

  // Handle sort change with proper field validation
  const handleTableSortChange = (orderBy: number, orderDirection: string) => {
    if (orderBy >= 0 && columns && columns[orderBy] && (orderDirection === 'asc' || orderDirection === 'desc')) {
      const field = String(columns[orderBy].field);
      // Only trigger sort change if field is valid and different from current state
      if (field && (field !== sortField || orderDirection !== sortOrder)) {
        handleSortChange(field, orderDirection);
      }
    }
  };

  const totalPages = Math.ceil(tableData.totalCount / rowsPerPage);

  // Render table
  return (
    <div className="organization-table-wrapper">
      <CheckboxPaddingStyles />
      <MatDataGrid
        key={stableTableKey}
        columns={columns}
        data={tableData.data}
        isLoading={loading}
        sorting
        enableSelection
        onSelectionChange={(rows) => setSelectedRows?.(rows)}
        onOrderChange={handleTableSortChange}
        headerStyle={{
          color: '#FFF',
          fontSize: '16px',
          height: 50,
          padding: '0px'
        }}
        rowStyle={{
          fontSize: '14px',
          wordWrap: 'break-word',
        }}
        customPagination={{
          itemsPerPage: rowsPerPage,
          setItemsPerPage: (newSize: number) => handleRowsPerPageChange(newSize),
          currentPage: currentPage + 1,
          setCurrentPage: (newPage: number) => handlePageChange(newPage),
          totalPages: totalPages > 0 ? totalPages : 1,
          baseRoute: 'organizations',
          shouldRedirect: false
        }}
      />
      {!isVendorOnlyUser && (
        <FlexboxContainerComponent margin="24px 0px">
          <Button
            borderColor={Color.error}
            background={Color.error}
            onClick={handleDeleteMultiple}
            disabled={!selectedRows?.length}
            permissionCode={PermissionCode.EDIT_REMOVE_BUSINESS_UNIT}
          >
            {GENERAL_CONSTANTS.DELETE_SELETED}
          </Button>
        </FlexboxContainerComponent>
      )}
    </div>
  );
};

export default BusinessUnitListTable;